import { AccrualMatchDisplay } from "@common/schemas/domains/accrualReview/accrual-review.schema";

interface CashMatchedDocument {
    id: string;
    invoiceNumber: string | null;
    total: number;
    createdAt?: Date;
    counterpartyName?: string | null;
}

export interface CashDocumentMatch {
    invoice?: CashMatchedDocument | null;
    bill?: CashMatchedDocument | null;
    invoiceId?: string | null;
    billId?: string | null;
}

interface ResolvedCashDocument {
    kind: "invoice" | "bill";
    id: string;
    document: CashMatchedDocument | null;
}

interface CashDocumentMatchesToPreviewParams {
    matches: readonly CashDocumentMatch[] | null | undefined;
    fallbackAmount: number;
}

type AccrualDocument = NonNullable<AccrualMatchDisplay["invoice"]>;

function resolveCashDocument(
    match: CashDocumentMatch,
): ResolvedCashDocument | null {
    if (match.invoice) {
        return {
            kind: "invoice",
            id: match.invoice.id,
            document: match.invoice,
        };
    }
    if (match.bill) {
        return { kind: "bill", id: match.bill.id, document: match.bill };
    }
    if (match.invoiceId) {
        return { kind: "invoice", id: match.invoiceId, document: null };
    }
    if (match.billId) {
        return { kind: "bill", id: match.billId, document: null };
    }
    return null;
}

function resolveCashDocuments(
    matches: readonly CashDocumentMatch[] | null | undefined,
): ResolvedCashDocument[] {
    return (matches ?? []).flatMap((match) => {
        const resolved = resolveCashDocument(match);
        return resolved ? [resolved] : [];
    });
}

function toAccrualDocument({
    resolved,
    fallbackAmount,
}: {
    resolved: ResolvedCashDocument;
    fallbackAmount: number;
}): AccrualDocument {
    if (!resolved.document) {
        return { id: resolved.id, invoiceNumber: null, total: fallbackAmount };
    }
    return {
        id: resolved.document.id,
        invoiceNumber: resolved.document.invoiceNumber,
        total: resolved.document.total,
        createdAt: resolved.document.createdAt,
        counterpartyName: resolved.document.counterpartyName,
    };
}

export function getCashLinkedDocumentIds(
    matches: readonly CashDocumentMatch[] | null | undefined,
): string[] {
    return resolveCashDocuments(matches).map((resolved) => resolved.id);
}

export function cashDocumentMatchesToPreview({
    matches,
    fallbackAmount,
}: CashDocumentMatchesToPreviewParams): AccrualMatchDisplay[] {
    return resolveCashDocuments(matches).map((resolved) => {
        const document = toAccrualDocument({ resolved, fallbackAmount });
        return resolved.kind === "invoice"
            ? { amount: document.total, invoice: document }
            : { amount: document.total, bill: document };
    });
}
